import React from 'react'

import type { ContainerProps, Theme } from '@mui/material'
import { Container, styled } from '@mui/material'

import type { PageLayoutProps, PageLayoutVariant } from './PageLayout.types'
import { toCssLength } from './PageLayout.styles'
import type { CSSObject } from '@mui/material/styles'

export interface PageLayoutContainerRootProps
  extends Omit<ContainerProps, 'variant'>,
    Pick<PageLayoutProps, 'contentBottomInset'> {
  variant?: PageLayoutVariant
}

/** The content column's gutters and alignment, for anything laid out beside it that must share its edges */
export const pageLayoutContainerGutters = (
  theme: Theme,
  { variant, disableGutters }: Pick<PageLayoutContainerRootProps, 'variant' | 'disableGutters'>,
): Record<string, CSSObject> => ({
  [theme.breakpoints.up('xs')]: {
    paddingLeft: disableGutters ? 0 : theme.spacing(4),
    paddingRight: disableGutters ? 0 : theme.spacing(4),
  },

  [theme.breakpoints.up('xl')]: {
    paddingLeft: disableGutters ? 0 : theme.spacing(8),
    paddingRight: disableGutters ? 0 : theme.spacing(8),
    ...((variant === 'two-column' || variant === 'two-column-with-sidebar') && { marginLeft: 0 }),
  },
})

// Page layout container is used across the entire app - update with caution!
const PageLayoutContainerRoot = styled(Container, {
  name: 'PageLayout',
  slot: 'Container',
  overridesResolver: (_props, styles) => styles.container,
  shouldForwardProp: (prop) => prop !== 'variant' && prop !== 'contentBottomInset',
})<PageLayoutContainerRootProps>(({ theme, variant, disableGutters, contentBottomInset }) => {
  const gutters = pageLayoutContainerGutters(theme, { variant, disableGutters })
  const allWidths = theme.breakpoints.up('xs')

  return {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    // No percentage height: in the layout's flex column it resolves against the parent's whole
    // height, banners and this margin included, and overflows the viewport on every short page
    minHeight: `calc(100vh - ${theme.spacing(18)}${contentBottomInset ? ` - ${toCssLength(contentBottomInset)}` : ''})`,
    marginTop: theme.spacing(4),
    ...gutters,
    // In the media block: the Container's gutter reset is a `padding: 0` shorthand inside a
    // media query, which would wipe a plain padding-bottom at real widths
    [allWidths]: { ...gutters[allWidths], paddingBottom: theme.spacing(4) },
  }
})

const PageLayoutContainer = React.memo(PageLayoutContainerRoot)

export default PageLayoutContainer
